import { Button, Spacing, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type Dispatch, type SetStateAction, memo, useCallback, useEffect } from 'react';
import type { AsidePollProps, PollResult, PollStep } from 'reactApp/ui/AsidePollModal/AsidePollModal.types';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './Poll.css';

type PollProps = Pick<AsidePollProps, 'allowBackButton' | 'pollQuestions' | 'analyticsPrefix'> & {
    setStep: Dispatch<SetStateAction<PollStep>>;
    setResults: Dispatch<SetStateAction<PollResult[]>>;
    step: PollStep;
    results: PollResult[];
};

const cyrillicVariants = 'абвгдеёжз';

export const Poll = memo<PollProps>(({ allowBackButton, pollQuestions = [], step, setStep, results, setResults, analyticsPrefix }) => {
    const { analyticsId, title, variants, multiple } = pollQuestions[step as number];

    const disableNext = multiple ? !results?.[step]?.length : results?.[step] === undefined;

    useEffect(() => {
        sendPaymentGa({
            action: 'show',
            eventCategory: ECategoryGa.balloon,
            name: `${analyticsPrefix}_${analyticsId}`,
        });
    }, [step, analyticsPrefix, analyticsId]);

    const handleTogglePollVariant = useCallback(
        (variant: number) => {
            if (multiple) {
                setResults((prev) => {
                    if (prev?.[step]?.includes(variant)) {
                        prev[step] = prev[step].filter((item) => item !== variant);
                    } else {
                        prev[step] = [...(prev?.[step] || []), variant];
                    }
                    return [...prev];
                });

                return;
            }

            setResults((prev) => {
                prev[step] = variant;
                return [...prev];
            });
        },
        [step, setResults, multiple]
    );

    const handlePrev = useCallback(() => {
        setStep((prev) => Number(prev) - 1);
    }, [setStep]);

    const handleNext = useCallback(() => {
        const answers = Array.isArray(results[step]) ? results[step] : [results[step]];

        for (const answer of answers) {
            sendPaymentGa({
                action: 'click',
                eventCategory: ECategoryGa.balloon,
                name: `${analyticsPrefix}_${analyticsId}`,
                answer: cyrillicVariants[answer],
            });
        }

        setStep((prev) => {
            if (prev === pollQuestions.length - 1) {
                return 'result';
            }

            return Number(prev) + 1;
        });
    }, [setStep, pollQuestions, analyticsPrefix, analyticsId, results, step]);

    return (
        <div className={styles.content}>
            <Title level="1" Component="span">
                {title}
            </Title>
            <Spacing size={6} />
            <Text className={styles.step}>
                {Number(step) + 1} из {pollQuestions?.length}
                {multiple && ', можно выбрать несколько'}
            </Text>
            <Spacing size={20} />
            {variants.map((item, index) => {
                const isActiveButton = multiple && results[step]?.length ? results[step].includes(index) : results[step] === index;

                return (
                    <Button
                        key={item}
                        mode="secondary"
                        align="left"
                        size="l"
                        appearance="neutral"
                        className={classNames(styles.pollButton, {
                            [styles.pollButtonActive]: isActiveButton,
                        })}
                        before={cyrillicVariants[index]}
                        stretched
                        onClick={() => handleTogglePollVariant(index)}
                    >
                        {item}
                    </Button>
                );
            })}
            <Spacing size={20} />
            <div className={styles.buttonsWrapper}>
                {Boolean(allowBackButton && step) && (
                    <Button mode="primary" appearance="accent-invariable" size="l" onClick={handlePrev}>
                        Назад
                    </Button>
                )}
                <Button mode="primary" appearance="accent-invariable" size="l" onClick={handleNext} disabled={disableNext}>
                    {step === pollQuestions.length - 1 ? 'Узнать результат' : 'Дальше'}
                </Button>
            </div>
        </div>
    );
});

Poll.displayName = 'Poll';
